import { useMemo } from 'react';
import { formatDate } from '../utils';

export default function DashboardVendorInsurance({ contacts, onNavigate }) {
  const { vendorCount, expired, expiringSoon, current, noDate, urgentVendor } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thirtyDaysOut = new Date(today);
    thirtyDaysOut.setDate(thirtyDaysOut.getDate() + 30);

    const vendors = (contacts || []).filter(c => c.insuranceExpiry || c.type === "Vendor");
    let expired = 0, expiringSoon = 0, current = 0, noDate = 0;
    let urgentVendor = null;

    for (const v of vendors) {
      if (!v.insuranceExpiry) {
        noDate++;
        continue;
      }
      const expiry = new Date(v.insuranceExpiry);
      expiry.setHours(0, 0, 0, 0);
      if (expiry < today) {
        expired++;
        if (!urgentVendor || expiry > new Date(urgentVendor.insuranceExpiry)) {
          urgentVendor = v;
        }
      } else if (expiry <= thirtyDaysOut) {
        expiringSoon++;
      } else {
        current++;
      }
    }
    return { vendorCount: vendors.length, expired, expiringSoon, current, noDate, urgentVendor };
  }, [contacts]);

  if (vendorCount === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-stone-800 mb-3">Vendor Insurance</h3>
        <p className="text-xs text-slate-400">No vendor insurance data. Add insurance expiry dates to contacts.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="text-sm font-semibold text-stone-800 mb-3">Vendor Insurance</h3>
      <div className="flex items-baseline gap-3 mb-3">
        <p className="text-2xl font-bold text-stone-700">{vendorCount}</p>
        <p className="text-xs text-slate-500">vendors tracked</p>
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {expired > 0 && (
          <button onClick={() => onNavigate("vendor-insurance", "expired")}
            className="text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-medium cursor-pointer hover:opacity-80 hover:shadow-[0_0_0_2px_rgba(232,160,32,0.3)] transition-all">
            {expired} Expired
          </button>
        )}
        {expiringSoon > 0 && (
          <button onClick={() => onNavigate("vendor-insurance", "expiring")}
            className="text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-medium cursor-pointer hover:opacity-80 hover:shadow-[0_0_0_2px_rgba(232,160,32,0.3)] transition-all">
            {expiringSoon} Expiring &lt;30d
          </button>
        )}
        {current > 0 && (
          <button onClick={() => onNavigate("vendor-insurance", "current")}
            className="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-medium cursor-pointer hover:opacity-80 hover:shadow-[0_0_0_2px_rgba(232,160,32,0.3)] transition-all">
            {current} Current
          </button>
        )}
        {noDate > 0 && (
          <button onClick={() => onNavigate("vendor-insurance", "noDate")}
            className="text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-medium cursor-pointer hover:opacity-80 hover:shadow-[0_0_0_2px_rgba(232,160,32,0.3)] transition-all">
            {noDate} No Date
          </button>
        )}
      </div>
      {urgentVendor && (
        <div className="border-t border-slate-100 pt-2">
          <button onClick={() => onNavigate("vendor-insurance")}
            className="text-xs text-red-600 font-medium cursor-pointer hover:bg-slate-50 rounded px-1 -mx-1 py-0.5 transition-colors group text-left w-full">
            ⚠ {urgentVendor.name || urgentVendor.company} — expired {formatDate(urgentVendor.insuranceExpiry, { month: "short", day: "numeric" })}
            <span className="text-[10px] font-semibold text-amber-500 bg-amber-50 px-1 rounded hidden group-hover:inline ml-1">→ Vendor Insurance Report</span>
          </button>
        </div>
      )}
    </div>
  );
}
